import { useContext, useEffect, useState } from 'react'
import IdeaContext from '../IdeaContext';
import {Text, Flex, Stack, Box, Tooltip, Menu, ActionIcon, Loader, Avatar} from '@mantine/core';
import {IconAlertOctagon, IconCheck, IconX} from '@tabler/icons';
import { StatusHeaderIcon, PriorityHeaderIcon, OwnerHeaderIcon, DeliveryHeaderIcon, ProductAreaHeaderIcon, DuplicateIcon, SparklesIcon } from '../../../icons/x-symbol-svgrepo-com';
import BagelAutocomplete from '../../general/BagelAutocomplete';
import { InfoCard } from '../../idea/InfoCard';
import { saveIdea } from '../../../Api';
import { useAuth0 } from '@auth0/auth0-react';
import {showNotification} from "@mantine/notifications";
import {SourceIcon} from "../../../utils/SourceIcon";
import { IdeaMenu } from './IdeaMenu';
import { LinkProduct } from './LinkProduct';
import BagelAccordionMenu from '../../general/BagelAccordionMenu';
import IdeaReleaseField from '../../releases/IdeaReleaseField';
import {useKnowledgeData} from "../../../hooks/useKnowledgeData";
import {useIdeaFilters} from "../../../hooks/useIdeaFilters";
import { getInitials } from '../../../utils/getInitials';
import {useFields} from "../../../hooks/useFields";


const IdeaAttributes = (
  {
    setVerifyOpened,
    duplicateIdea
  }: {
    setVerifyOpened: (value: boolean) => void;
    duplicateIdea: any;
  }
) => {

  const {
    components,
    allComponents,
    ideaPriorities,
    ideaStatuses,
    enableEditOnImportedIdeas,
    markedDeliveryStatus,
    overwriteIdeaStatusWithLinkedDelivery
  } = useFields();

  const { owners, deliveryPlatform, domains, displayState } = useKnowledgeData();

  const { idea, isGenerated } = useContext(IdeaContext);

  const auth0 = useAuth0();

  const domainsNames = (domains || []).map((d: any) => d.name)
  const selectedStatus = ideaStatuses?.find(
    (status: any) => status.key?.toLowerCase() == idea?.status?.toLowerCase()
  );
  const showRequestProductLink = true//TODO: get this from proper place
  const isStatusInDelivery: boolean =
    (selectedStatus?.key ==
    ideaStatuses?.filter((s: any) => s.isDefaultDelivery)?.[0]?.key) || idea?.linkedProductItemId;
  const selectableStatuses =
    ideaStatuses?.filter((i: any) => !i?.isDefaultDelivery) || [];

  const [isLoading, setIsLoading] = useState<{
    [key: string]: boolean,
  }>({});
  const [priority, setPriority] = useState(idea?.priority || 'Unassigned')
  const [domain, setDomain] = useState(idea?.componentObj?.domain || 'Unassigned');
  const [componentName, setComponentName] = useState(idea?.componentObj?.name || 'Unassigned');
  const [component, setComponent] = useState(idea?.componentObj);
  const [componentIdSource, setComponentIdSource] = useState(idea?.componentIdSource);
  const [ownerName, setOwnerName] = useState(idea?.owner?.name || idea?.ownerName || 'Unassigned');
  const [ownerId, setOwnerId] = useState<string | null>(idea?.owner?._id || null);
  const [ownerPicture, setOwnerPicture] = useState(idea?.owner?.picture || '');
  const [status, setStatus] = useState(idea?.status || 'Unassigned');
  const [deliveryStatus, setDeliveryStatus] = useState(idea?.productItem?.status || 'Unassigned');
  const [componentsByDomain, setComponentsByDomain] = useState<any[]>(idea?.components);
  const [loadingSync, setLoadingSync] = useState(true);
  const hasOrigin: boolean =
    idea?.origin === "jira" ||
    idea?.origin === "clickup" ||
    idea?.origin === "productboard";

  const deliveryStatusStr = typeof deliveryStatus === 'string' ? deliveryStatus : String(deliveryStatus ?? '');
  const isStatusLinked: boolean =
    !!deliveryStatusStr &&
    !!markedDeliveryStatus?.some((s: any) =>
      s.isStatusLink === true &&
      (s.name?.toLowerCase() === deliveryStatusStr.toLowerCase() ||
        s.id === deliveryStatus ||
        s.key?.toLowerCase() === deliveryStatusStr.toLowerCase())
    );
  const showLinkedDeliveryInStatus = overwriteIdeaStatusWithLinkedDelivery && isStatusLinked;

  const onSelectSave = async (type: string, value: string, ownerId = null, domain = null) => {
    if (idea) {
      setIsLoading((prev) => ({...prev, [type]: true}))
      const ideaObj = {
        id: idea._id,
        [type]: value,
      };
      if (ownerId) {
        ideaObj['ownerId'] = ownerId
        setIsLoading((prev) => ({...prev, ["ownerId"]: true}))

        ideaObj['ownerIdSource'] = "user";
      }
      if (type === 'componentId' && domain) {
        ideaObj['domain'] = domain
      }
      if (type === 'componentId') {
        ideaObj['componentIdSource'] = "user";
        ideaObj['ownerIdSource'] = "user";
      }
      
      const response = saveIdea(ideaObj, auth0).then((res) => {
        setIsLoading((prev) => ({...prev, [type]: false}))
        if (ownerId) {
          setIsLoading((prev) => ({...prev, ["ownerId"]: false}))
        }
        if (type === 'componentId') {
          setComponentIdSource("user");
        }
        return res
      })
      return response
    }
  };

  const filterComponentByDomain = (domainValue: string) => {
    const copy = [...components];
    const arr: any[] = copy.filter((s: any) => s.domain === domainValue) || [];
    setComponentsByDomain(arr);
  };

  useEffect(() => {
    setPriority(idea?.priority || 'Unassigned')
    setDomain(idea?.componentObj?.domain || 'Unassigned')
    setOwnerName(idea?.owner?.name || 'Unassigned')
    setOwnerId(idea?.owner?._id || null)
    setOwnerPicture(idea?.owner?.picture || '')
    setStatus(idea?.status || 'Unassigned');
    setDeliveryStatus(idea?.productItem?.status || 'Unassigned');
    setComponentsByDomain(idea?.components)
    setComponentName(idea?.componentObj?.name || 'Unassigned')
    setComponent(idea?.componentObj)
    setComponentIdSource(idea?.componentIdSource)
  }, [idea]);

  const domainComponentMap = (domains || []).map((domain: any) => {
    const childComponents = (components || [])
      .filter((comp: any) => comp.domain?.toLowerCase()?.trim() === domain.name?.toLowerCase()?.trim())
      .map((comp: any) => ({
        ...comp,
        id: comp._id,
        childComponents: comp.childComponents || []
      }));

    return {
      ...domain,
      id: domain._id,
      name: domain.name,
      ownerName: domain.ownerName,
      productComponents: childComponents
    };
  });

  // console.log({componentObj:idea?.componentObj})
  const isAiSuggestion = idea?.componentObj?._id && componentIdSource === 'ml';



  return (

    <Flex align='center' justify='flex-end'>
      
      {/* DuplicateIdea*/}

      {duplicateIdea && (
        <Box>
          <Flex ml={"4px"} mb={"4px"}>
            <DuplicateIcon/>
            <Text fz={'12px'}  color={'#585C68'} ml={"6px"} sx={{ lineHeight: '16px' }}>Duplication</Text>
          </Flex>
          <Menu>
            <Menu.Target>
              <Tooltip label={
                <Box p={0.186}>
                  <Text fz={11.4}mb={1.4}>Duplication alert</Text>
                </Box>
              }>
                <Flex
                  h={24}
                  px={8}
                  py={4}
                  mx={"2px"}
                  bg="#DB25251A"
                  justify="space-evenly"
                  align="center"
                  sx={{ borderRadius: "4px", border: "0.8px solid #F8e9FB", cursor: 'pointer' }}
                  onClick={() => setVerifyOpened(true)}
                >
                  <Text fz="12px"  color={'Red'}  lineClamp={1} maw="110px" miw={"75px"}>
                    Duplication
                  </Text>
                </Flex>
              </Tooltip>
            </Menu.Target>
          </Menu>
        </Box>
      )} 

      {/* Product area */}

      {displayState?.component && (
        <Box>
          <Flex ml={"4px"} align="center" mb={"4px"}>
            <ProductAreaHeaderIcon />
            <Text fz={'12px'} color={'#585C68'} ml={"6px"} sx={{ lineHeight: '16px' }}>Product Area</Text>
          </Flex>
          <BagelAccordionMenu
            // disabled={isGenerated}
            selectedComponent={component}
            isAiSuggestion={isAiSuggestion}
            loading={loadingSync}
            onSelect={async (item: any, type: string) => {
              if (type === 'Domain') {
                setDomain(item.name);
                // const domainObject = domains.find((d: any) => { return d.name == item.name })
                // if (domainObject.ownerId) {
                //   setOwnerName(domainObject.ownerName)
                //   setOwnerId(domainObject.ownerId)
                // }
                // filterComponentByDomain(item)
              } else {
                const oldValue = componentName
                setComponentName(item.name)
                // Show loading for owner if component has an owner
                if (item.ownerId) {
                  setIsLoading((prev) => ({...prev, ownerId: true}))
                }
                const res = await onSelectSave('componentId', item._id || '', item.ownerId, item.domain)

                console.log({allComponents, item: allComponents.find((c: any) => c._id === item._id), res})
                if (res?.data.success !== true) {
                  setComponentName(oldValue)
                  showNotification({
                    title: "Error saving product area",
                    color: "red",
                    message: "please try again...",
                    icon: <IconX size="1.1rem" />,
                    autoClose: 1200
                  });
                } else {
                  if (item.ownerId) {
                    const newOwner = owners.find((o: any) => o._id === item.ownerId);
                    setOwnerName(item.ownerName)
                    setOwnerId(item.ownerId)
                    setOwnerPicture(newOwner?.picture || '')
                  }
                  setComponent(allComponents.find((c: any) => c._id === item._id))
                  setIsLoading((prev) => ({...prev, ownerId: false}))
                  showNotification({
                    title: "Product area saved",
                    color: "green",
                    message: "Product area saved successfully",
                    icon: <IconCheck size="1.1rem" />,
                    autoClose: 1200
                  });
                }
              }
            }}
          >
            <Flex
              h={24}
              px={8}
              py={4}
              mx={"2px"}
              bg="#5C5CEB1A"
              justify="space-evenly"
              align="center"
              sx={{ borderRadius: "4px", border: "0.8px solid #F8e9FB", cursor: isGenerated ? 'default' : 'pointer' }}
            >
              <Flex style={{display: "flex", alignItems: "center"}}>
                {isAiSuggestion && (
                  <Box mb={-6} mr={5}>
                    <SparklesIcon color={'#5B60E3'}/>
                  </Box>
                )}
                <Text fz="12px" lineClamp={1} maw="150px" tt="capitalize" miw={"85px"}
                  sx={() => ({
                    color: isAiSuggestion
                      ? "#5C5CEB"
                      : !componentName || componentName === "Unassigned"
                        ? "red"
                        : "black",
                    animation:
                      componentName && componentName !== "Unassigned"
                        ? "none"
                        : "blinking 1s 5",
                    '@keyframes blinking': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0 },
                      '100%': { opacity: 1 },
                    },
                  })}
                >
                  {componentName && componentName !== "Unassigned" && <Text span color='dimmed'>.../ </Text>} {componentName || "Unassigned"}
                </Text>
              </Flex>
              {isLoading['componentId'] && <Loader pl={4} size={14} color='#5C5CEB'/>}
            </Flex>
          </BagelAccordionMenu>
        </Box>
      )}

      {/* Release — the idea's own releases, plus the linked delivery item's
          shown read-only. Defaults off; toggled in Objects & fields → Ideas → Display. */}
      {displayState?.release && (
        <IdeaReleaseField idea={idea} />
      )}

      {/* Owner */}
      <Box>
        <Flex ml={"4px"} align="center" mb={"4px"}>
          <OwnerHeaderIcon />
          <Text fz={'12px'} color={'#585C68'} ml={"6px"} sx={{ lineHeight: '16px' }}>Owner</Text>
        </Flex>
        <BagelAutocomplete
          // disabled={isGenerated}
          items={owners.map((member: any) => ({
            name: member.name,
            id: member._id,
            useId: true,
            email: member.email,
            picture: member.picture
          }))}
          selected={ownerId}
          onSelect={async (item: any) => {
            const res = await onSelectSave('ownerId', item.id)
            if (res?.data?.success !== true) {
              showNotification({
                title: "Error saving owner",
                color: "red",
                message: "please try again...",
                icon: <IconX size="1.1rem" />,
                autoClose: 1200
              });
            } else {
              setOwnerId(item?.id);
              setOwnerName(item?.name);
              setOwnerPicture(item?.picture || '');
              showNotification({
                title: "Owner saved",
                color: "green",
                message: "Owner saved successfully",
                icon: <IconCheck size="1.1rem" />,
                autoClose: 1200
              });
            }
          }}
        >
          <Tooltip
            label={
              <Box p={0.186}>
                <Text fz={11.4} mb={1.4}>Owner:</Text>
                <Text fz={11.4}>{ownerName}</Text>
              </Box>

            }>
            <Flex
              h={24}
              px={8}
              py={4}
              mx={"2px"}
              bg="#5C5CEB1A"
              justify="space-evenly"
              align="center"
              gap={6}
              sx={{ borderRadius: "4px", border: "0.8px solid #F8e9FB", cursor: isGenerated ? 'default' : 'pointer' }}
            >
              {isLoading['ownerId'] ? (
                <Loader size={14} color='#5C5CEB'/>
              ) : ownerName && ownerName !== 'Unassigned' ? (
                <Avatar 
                  src={ownerPicture} 
                  radius="xl" 
                  size={16}
                >
                  {getInitials(ownerName)}
                </Avatar>
              ) : null}
              <Text fz="12px" lineClamp={1} maw="150px" miw={"100px"}>
                {ownerName || ""}
              </Text>
            </Flex>
          </Tooltip>
        </BagelAutocomplete>
      </Box>
      {/* status */}

    

      {displayState?.status && (
        <Box>
          <Flex ml={"4px"} mb={"4px"}>
            <StatusHeaderIcon/>
            <Text fz={'12px'} color={'#585C68'} ml={"6px"} sx={{ lineHeight: '16px' }}>Status</Text>
          </Flex>
          <Menu shadow="md" width={200} disabled={showLinkedDeliveryInStatus || isStatusInDelivery || isGenerated}>
            <Menu.Target>
              <Tooltip label={
                <Box p={0.186}>
                  <Text fz={11.4} mb={1.4}>Status:</Text>
                  <Text fz={11.4}>
                    {showLinkedDeliveryInStatus
                      ? (deliveryStatus || idea?.productItem?.status || status)
                      : status}
                  </Text>
                </Box>
              }>
                {showLinkedDeliveryInStatus ? (
                  <Box
                    component="a"
                    href={idea?.productItem?.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Flex
                      h={24}
                      px={8}
                      py={4}
                      bg="#5C5CEB1A"
                      mx={"2px"}
                      justify="center"
                      align="center"
                      gap={4}
                      sx={{ borderRadius: "4px", border: "0.8px solid #F8e9FB", cursor: 'pointer' }}
                    >
                      <SourceIcon
                        sourceName={idea?.productItem?.origin || deliveryPlatform}
                        width={14}
                        height={14}
                      />
                      <Text fz="12px" lineClamp={1} style={{ width: "70px" }} tt="capitalize" miw={"65px"}>
                        {deliveryStatus || idea?.productItem?.status || status}
                      </Text>
                    </Flex>
                  </Box>
                ) : (
                  <Flex
                    h={24}
                    px={8}
                    py={4}
                    bg="#5C5CEB1A"
                    mx={"2px"}
                    justify="center"
                    align="center"
                    sx={{ borderRadius: "4px", border: "0.8px solid #F8e9FB", cursor: (hasOrigin || isStatusInDelivery || isGenerated) ? "default" : 'pointer'}}
                  >
                    <Text fz="12px" lineClamp={1} style={{width: "70px"}} tt="capitalize" miw={"65px"}>
                      {status}
                    </Text>
                    {isLoading['status'] && <Loader pl={4} size={14} color='#5C5CEB'/>}
                  </Flex>
                )}
              </Tooltip>
            </Menu.Target>

            {(enableEditOnImportedIdeas || !hasOrigin) && (
              <Menu.Dropdown>
                <Menu.Label>Choose one...</Menu.Label>
                {selectableStatuses &&
                              selectableStatuses.length > 0 &&
                              selectableStatuses?.map((item: any) => (
                                <Menu.Item
                                  key={item.key}
                                  onClick={async () => {
                                    const oldValue = status
                                    // setStatus(item.key) 
                                    const res = await onSelectSave("status", item.key)
                                    if (res?.data?.success !== true) {
                                      setStatus(oldValue)
                                      showNotification({
                                        title: "Error saving status",
                                        color: "red",
                                        message: "please try again...",
                                        icon: <IconX size="1.1rem" />,
                                        autoClose: 1200
                                      });
                                    } else {
                                      setStatus(item.key)
                                      showNotification({
                                        title: "Status saved",
                                        color: "green",
                                        message: "Status saved successfully",
                                        icon: <IconCheck size="1.1rem" />,
                                        autoClose: 1200
                                      });
                                    }
                                  }}
                                  tt={"capitalize"}
                                  bg={item?.label === status ? "#f0f0f0" : "transparent"}
                                >
                                  {item?.label}
                                </Menu.Item>
                              ))}
              </Menu.Dropdown>
            )}
          </Menu>
        </Box>
      )} 

      {/* Priority */}

      {displayState?.priority && 
      <Box>
        <Flex ml={"4px"} align="center" mb={"4px"}>
          <PriorityHeaderIcon/>
          <Text fz={'12px'} color={'#585C68'} ml={"6px"} sx={{ lineHeight: '16px' }}>Priority</Text>
        </Flex>
        <Menu disabled={isGenerated} >
          <Menu.Target>
            <Tooltip label={
              <Box p={0.186}>
                <Text fz={11.4} mb={1.4}>Priority:</Text>
                <Text fz={11.4}>{priority}</Text>
              </Box>
            
            }>
              <Flex
                h={24}
                px={8}
                py={4}
                mx={"2px"}
                bg="#FFFAF2"
                justify="space-evenly"
                align="center"
                sx={{ borderRadius: "4px", border: "0.8px solid #F8e9FB" ,cursor: isGenerated ? 'default' : 'pointer'}}
              >
                <Box mt="4px" mr={5}>
                  <IconAlertOctagon size={14} color='#FF8700'/>
                </Box>
                <Text fz="12px" lineClamp={1} maw="120px"  tt="capitalize" miw={"75px"} color={"#FF8700"}>
                  {priority || ""}
                </Text>
                {isLoading['priority'] && <Loader pl={4} size={14} color='#FF8700'/>}
              </Flex>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            {ideaPriorities?.map((item: any) => (
              <Menu.Item
                w="180px"
                key={item}
                onClick={async () => {
                  const oldValue = priority
                  // setPriority(item.label);
                  const res = await onSelectSave("priority", item.label)
                  if (res?.data?.success !== true) {
                    setPriority(oldValue)
                    showNotification({
                      title: "Error saving priority",
                      color: "red",
                      message: "please try again...",
                      icon: <IconX size="1.1rem" />,
                      autoClose: 1200
                    });
                  } else {
                    setPriority(item.label)
                    showNotification({
                      title: "Priority saved",
                      color: "green",
                      message: "Priority saved successfully",
                      icon: <IconCheck size="1.1rem" />,
                      autoClose: 1200
                    });
                  }
                }}
                bg={item?.label === priority ? "#f0f0f0" : "transparent"}
              >
                {item.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>  
      </Box>
      }

      {displayState?.delivery && deliveryPlatform ? (
        <Box>
          <Flex ml={"4px"} align="center" mb={"4px"}>
            <DeliveryHeaderIcon/>
            <Text fz={'12px'} color={'#585C68'} miw={"85px"} ml={"6px"} sx={{ lineHeight: '16px' }}>Delivery</Text>
          </Flex>
          <LinkProduct
            request={idea}
            showRequestProductLink={showRequestProductLink}
          />
        </Box>
      ) : (
        displayState?.delivery && (
          <ActionIcon loading={loadingSync} >
            <InfoCard
              label={deliveryPlatform}
              text="Link"
              icon={
                <SourceIcon
                  sourceName={deliveryPlatform}
                  width={25}
                  height={25}
                />
              }
              cardProps={{ opacity: 0.5 }}
              isGenerated={isGenerated}
            />
          </ActionIcon>
        )
      )} 
      {<Stack align='center' mt={"18px"} ml={"2px"}>
        <IdeaMenu/>
      </Stack>
      }
      
    </Flex>

  );
}

export default IdeaAttributes
